import { Component, Inject } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { PredefinedEmployeeSetting } from "../../settings/settings.interface";
import type { Image } from "../../media/media.interface";
import {
  HARDCODED_VARIABLE_ICONS,
  hardcodedIconByKey,
  hardcodedIconByValue,
  hardcodedIconForVariable,
} from "../../digital-cards/variable-icons";
import type { HardcodedVariableIcon } from "../../digital-cards/digital-cards.interface";
import {
  EmployeeSettingsFieldSource,
  EmployeeSettingsFormDialogData,
  EmployeeSettingsFormDialogResult,
} from "../employee-settings.interface";
import { mediaFilePreview } from "../../shared/utils/media-file-preview.util";

@Component({
  selector: "app-employee-settings-form-dialog",
  templateUrl: "./employee-settings-form-dialog.component.html",
  styleUrls: ["./employee-settings-form-dialog.component.scss"],
  standalone: false,
})
export class EmployeeSettingsFormDialogComponent {
  public errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  public predefinedEmployeeSettings: PredefinedEmployeeSetting[];
  public hardcodedIcons: HardcodedVariableIcon[] = HARDCODED_VARIABLE_ICONS;
  public mediaImages: Image[];
  readonly mediaFilePreview = mediaFilePreview;

  public form: FormGroup<{
    source: FormControl<EmployeeSettingsFieldSource>;
    predefinedName: FormControl<string>;
    name: FormControl<string>;
    label: FormControl<string>;
    iconUrl: FormControl<string>;
  }>;

  private existingFieldNames: string[];

  constructor(
    public dialogRef: MatDialogRef<
      EmployeeSettingsFormDialogComponent,
      EmployeeSettingsFormDialogResult | undefined
    >,
    @Inject(MAT_DIALOG_DATA)
    public data: EmployeeSettingsFormDialogData,
  ) {
    this.existingFieldNames =
      data.existingFieldNames?.map((n) => n.trim().toLowerCase()) ?? [];
    this.predefinedEmployeeSettings = (
      data.predefinedEmployeeSettings ?? []
    ).filter((setting) => !this.isExistingFieldName(setting.name));
    this.mediaImages = data.mediaImages ?? [];

    const initialSource =
      this.predefinedEmployeeSettings.length > 0 ? "predefined" : "custom";
    const initialIcon =
      initialSource === "predefined" && this.predefinedEmployeeSettings[0]
        ? this.predefinedEmployeeSettings[0].iconUrl?.trim() ||
          hardcodedIconForVariable(this.predefinedEmployeeSettings[0].name)
            .value
        : hardcodedIconByKey("customLink").value;

    this.form = new FormGroup({
      source: new FormControl(initialSource, { nonNullable: true }),
      predefinedName: new FormControl("", { nonNullable: true }),
      name: new FormControl("", { nonNullable: true }),
      label: new FormControl("", { nonNullable: true }),
      iconUrl: new FormControl(initialIcon, {
        nonNullable: true,
        validators: [(control) => Validators.required(control)],
      }),
    });
    this.applySourceValidators(initialSource);
  }

  get isPredefinedSource(): boolean {
    return this.form.controls.source.value === "predefined";
  }

  get isCustomSource(): boolean {
    return this.form.controls.source.value === "custom";
  }

  iconSrc(url: string | null | undefined): string {
    if (!url) {
      return "";
    }
    return hardcodedIconByValue(url)?.preview ?? url;
  }

  iconLabel(url: string | null | undefined): string {
    if (!url) {
      return "";
    }
    const hardcoded = hardcodedIconByValue(url);
    if (hardcoded) {
      return hardcoded.label;
    }
    return (
      this.mediaImages.find((image) => image.imageUrl === url)?.name?.trim() ||
      url
    );
  }

  sourceChanged = (source: EmployeeSettingsFieldSource): void => {
    this.applySourceValidators(source);
    this.form.patchValue({
      predefinedName: "",
      name: "",
      label: "",
      iconUrl: hardcodedIconByKey("customLink").value,
    });
  };

  predefinedSettingChanged = (name: string): void => {
    const setting = this.findPredefinedSetting(name);

    if (!setting) {
      this.form.patchValue({
        name: "",
        label: "",
        iconUrl: hardcodedIconByKey("customLink").value,
      });
      return;
    }

    this.form.patchValue({
      name: setting.name,
      label: this.getPredefinedLabel(setting),
      iconUrl:
        setting.iconUrl?.trim() || hardcodedIconForVariable(setting.name).value,
    });
  };

  submit = () => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }

    const source = this.form.controls.source.value;
    const iconUrl = this.form.controls.iconUrl.value;

    if (source === "predefined") {
      const setting = this.findPredefinedSetting(
        this.form.controls.predefinedName.value,
      );

      if (!setting) {
        this.form.controls.predefinedName.setErrors({ required: true });
        return;
      }

      this.dialogRef.close({
        name: setting.name,
        label: this.getPredefinedLabel(setting),
        defaultValue: "",
        iconUrl,
        overridable: setting.overridableDefault ?? true,
        mandatory: setting.mandatoryDefault ?? false,
      });
      return;
    }

    this.dialogRef.close({
      name: this.form.controls.name.value.trim(),
      label: this.form.controls.label.value.trim(),
      defaultValue: "",
      iconUrl,
      overridable: true,
      mandatory: false,
    });
  };

  close = (): void => {
    this.dialogRef.close(undefined);
  };

  private applySourceValidators(source: EmployeeSettingsFieldSource): void {
    const { predefinedName, name, label } = this.form.controls;

    if (source === "predefined") {
      predefinedName.setValidators((control) => Validators.required(control));
      name.clearValidators();
      label.clearValidators();
    } else {
      predefinedName.clearValidators();
      name.setValidators([
        (control) => Validators.required(control),
        (control) => this.duplicateFieldValidator(control),
      ]);
      label.setValidators((control) => Validators.required(control));
    }

    predefinedName.updateValueAndValidity();
    name.updateValueAndValidity();
    label.updateValueAndValidity();
  }

  private duplicateFieldValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const value = String(control.value ?? "")
      .trim()
      .toLowerCase();

    if (!value) {
      return null;
    }

    return this.isExistingFieldName(value) ? { duplicateField: true } : null;
  }

  private findPredefinedSetting(
    name: string,
  ): PredefinedEmployeeSetting | undefined {
    return this.predefinedEmployeeSettings.find((setting) => {
      return setting.name === name;
    });
  }

  private getPredefinedLabel(setting: PredefinedEmployeeSetting): string {
    return setting.label?.trim() || setting.name;
  }

  private isExistingFieldName(name: string): boolean {
    return this.existingFieldNames.includes(name.trim().toLowerCase());
  }
}
